import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import {
  useSubscription,
  useCalendarEvents,
  useGmail,
  useCheckoutPolling,
  useGmailOAuthCallback,
  useTaskExitAnimation,
  useVoiceIntegration,
  useConnectionToasts,
  useTodoTaskEditing,
  useListTagManagement,
  useTodoAppGlobalShortcuts,
  useRealtimeSync,
  useEmailPageUrlSync, useGmailAccountUrlSync,
} from "./hooks";
import { useTasks } from "./hooks/queries/useTasks";
import { useLists } from "./hooks/queries/useLists";
import { useTags } from "./hooks/queries/useTags";
import { useFilters } from "./hooks/queries/useFilters";
import { useCreateTask } from "./hooks/mutations/useCreateTask";
import { useUpdateTask } from "./hooks/mutations/useUpdateTask";
import { useDeleteTask } from "./hooks/mutations/useDeleteTask";
import {
  useDeleteSubtask,
  useDeleteSubtasks,
} from "./hooks/mutations/useDeleteSubtask";
import { useCreateSubtask } from "./hooks/mutations/useCreateSubtask";
import { useUpdateSubtask } from "./hooks/mutations/useUpdateSubtask";
import { useMoveSubtask } from "./hooks/mutations/useMoveSubtask";
import { useConvertSubtaskToTask } from "./hooks/mutations/useConvertSubtaskToTask";
import { useConvertTaskToSubtask } from "./hooks/mutations/useConvertTaskToSubtask";
import { useToggleTask } from "./hooks/mutations/useToggleTask";
import { useArchiveTask } from "./hooks/mutations/useArchiveTask";
import { useCreateList } from "./hooks/mutations/useCreateList";
import { useUpdateList } from "./hooks/mutations/useUpdateList";
import { useDeleteList } from "./hooks/mutations/useDeleteList";
import { useMoveListTasks } from "./hooks/mutations/useMoveListTasks";
import { useDeleteAllTasksInList } from "./hooks/mutations/useDeleteAllTasksInList";
import { useCreateTag } from "./hooks/mutations/useCreateTag";
import { useUpdateTag } from "./hooks/mutations/useUpdateTag";
import { useDeleteTag } from "./hooks/mutations/useDeleteTag";
import { useCreateFilter } from "./hooks/mutations/useCreateFilter";
import { useUpdateFilter } from "./hooks/mutations/useUpdateFilter";
import { useSetTaskTags } from "./hooks/mutations/useSetTaskTags";
import { useSetSubtaskTags } from "./hooks/mutations/useSetSubtaskTags";
import { saveReviewedTodos } from "./lib/saveReviewedTodos";
import { useGoogleCalendar } from "./contexts/useGoogleCalendar";
import { useAuth } from "./contexts/AuthContext";
import { useDialog } from "./contexts/DialogContext";
import { ViewProvider, useView } from "./contexts/ViewContext";
import { Task, NewTask, TaskCapture } from "./types";
import { ProposedTodo } from "./hooks/useVoiceToTodo";
import { emptyNewTask } from "./lib/emptyNewTask";
import { createdFromEmailLabels } from "./lib/emailArchiveToast";
import { countTasksWithTag, parseDateFromTitle } from "./lib/utils";
import { formatLocalDate } from "./lib/workingHours";
import {
  Header,
  TaskDialog,
  AddListDialog,
  AddTagDialog,
  SettingsDialog,
  DeleteConfirmDialog,
  CompleteWithSubtasksDialog,
  FloatingActionButton,
  SearchDialog,
  VoiceReviewDialog,
  KeyboardShortcutsDialog,
  UpgradeDialog,
} from "./components";
import FilterDialog from "./components/dialogs/FilterDialog";
import TaskNotFound from "./components/task/TaskNotFound";
import PWAInstallPrompt from "./components/misc/PWAInstallPrompt";
import TaskPickerDialog from "./components/dialogs/TaskPickerDialog";
import RecurringChangeWarningDialog from "./components/dialogs/RecurringChangeWarningDialog";
import AppLoader from "./components/layout/AppLoader";
import CalendarViewSkeleton from "./components/skeletons/CalendarViewSkeleton";
import ViewLoadingSkeleton from "./components/skeletons/ViewLoadingSkeleton";
import BoardView from "./components/task/BoardView";
import AnimatePresence from "./components/ui/AnimatePresence";
import { withChunkReloadGuard } from "./lib/chunkReloadGuard";

// The board is the default view and stays in the initial payload; the other
// three are route-scoped and load when the user navigates to them. Each import
// carries the reload guard because a stale service-worker cache can otherwise
// leave a returning user pointed at a chunk filename that no longer exists.
const CalendarView = lazy(
  withChunkReloadGuard("calendarChunkReloaded", () =>
    import("./components/calendar/CalendarView"),
  ),
);

const EmailsViewShell = lazy(
  withChunkReloadGuard("emailsChunkReloaded", () =>
    import("./components/emails/EmailsViewShell"),
  ),
);

const CapturesView = lazy(
  withChunkReloadGuard("capturesChunkReloaded", () =>
    import("./components/captures/CapturesView").then((m) => ({
      default: m.CapturesView,
    })),
  ),
);

const TodoAppContent = () => {
  const { user } = useAuth();
  const {
    isConnected: isGoogleCalendarConnected,
    connectGoogleCalendar,
    connection: googleCalendarConnection,
  } = useGoogleCalendar();
  const {
    isPro,
    subscription,
    plan: subscriptionPlan,
    createCheckoutSession,
    createPortalSession,
    refetch: refetchSubscription,
  } = useSubscription(); const emailPageUrl = useEmailPageUrlSync(); // seeds useGmail's initial account below
  const {
    accounts: gmailAccounts,
    accountCounts: gmailAccountCounts,
    activeAccount: activeGmailAccount,
    setActiveAccount: setActiveGmailAccount,
    reconnectNeeded: gmailReconnectNeeded,
    isConnected: gmailConnected,
    isLoadingConnection: isLoadingGmailConnection,
    threads: gmailThreads,
    isFetchingEmails: isFetchingGmail,
    fetchEmails: fetchGmailEmails,
    fetchThread: fetchGmailThread,
    fetchAttachment: fetchGmailAttachment,
    archiveEmails: archiveGmailEmails,
    connectGmail,
    handleOAuthCallback: handleGmailOAuthCallback,
    reloadAccounts: reloadGmailAccounts,
  } = useGmail(emailPageUrl.initialAccountRef);
  const handleGmailAccountChange = useCallback((account: string, navKind?: "push" | "replace") => { setActiveGmailAccount(account); emailPageUrl.onAccountChange(account, navKind); }, [setActiveGmailAccount, emailPageUrl]); // mirrors into the URL too, deliberate picks push
  useGmailAccountUrlSync({ accounts: gmailAccounts, activeAccount: activeGmailAccount, urlAccount: emailPageUrl.urlAccount, setActiveAccount: setActiveGmailAccount, onAccountResolved: emailPageUrl.onAccountResolved }); // and follows it back on back/forward, and names the default account once it resolves
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate() as any;
  const { taskId } = useParams({ strict: false }) as { taskId?: string };
  const { subtask: activeSubtaskId } = useSearch({ strict: false }) as {
    subtask?: string;
  };

  // View state — the active view, the filters narrowing it, the search term,
  // the sort directions and the visibility toggles all live in ViewContext.
  const {
    viewType,
    activeFilterId,
    builtInFilterState,
    hasBuiltInFilters,
    setBuiltInFilterState,
    searchTerm,
    setSearchTerm,
    toggleShowCompleted,
    toggleAllSubtasksExpanded,
  } = useView();

  // Source tasks from the TanStack Query cache — the single source of truth
  // that every mutation hook writes back into.
  const { data: tasks = [], isPending: tasksPending } = useTasks();

  // Task mutation hooks.
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const { deleteTask: tqDeleteTask } = useDeleteTask();
  const tqDeleteSubtask = useDeleteSubtask();
  const tqDeleteSubtasks = useDeleteSubtasks();
  const { toggleTask: tqToggleTask } = useToggleTask();
  const { archiveTask: tqArchiveTask } = useArchiveTask();

  // Adapter matching the (id, updates, options?) call signature so
  // useTaskEditing can consume the mutation without knowing about the
  // { id, updates, options } object shape.
  const tqUpdateTask = useCallback(
    (
      id: string,
      updates: Partial<Task>,
      options?: { confirmedRecurringDateChange?: boolean },
    ) => updateTaskMutation.mutateAsync({ id, updates, options }),
    [updateTaskMutation],
  );

  // Lists, tags, and filters come from TanStack Query, which owns their fetch.
  const { data: lists = [], isPending: listsPending } = useLists();
  const { data: tags = [], isPending: tagsPending } = useTags();
  const { data: filters = [], isPending: filtersPending } = useFilters();

  // The app's render gate — the queries whose data the app actually renders.
  const isLoading =
    tasksPending || listsPending || tagsPending || filtersPending;

  // List mutation hooks.
  const createListMutation = useCreateList();
  const updateListMutation = useUpdateList();
  const deleteListMutation = useDeleteList();
  const moveListTasksMutation = useMoveListTasks();
  const deleteAllTasksInListMutation = useDeleteAllTasksInList();

  // Tag mutation hooks.
  const createTagMutation = useCreateTag();
  const updateTagMutation = useUpdateTag();
  const deleteTagMutation = useDeleteTag();

  // Filter mutation hooks.
  const createFilterMutation = useCreateFilter();
  const updateFilterMutation = useUpdateFilter();

  // Adapters that preserve the (id, data) call signatures expected by
  // useListTagManagement and the components that still use them directly.
  const addListToDb = useCallback(
    async (data: { name: string; emoji: string }) => {
      try {
        return await createListMutation.mutateAsync(data);
      } catch {
        return null;
      }
    },
    [createListMutation],
  );
  const updateListInDb = useCallback(
    async (id: string, updates: { name?: string; emoji?: string }) => {
      try {
        await updateListMutation.mutateAsync({ id, updates });
        return true;
      } catch {
        return false;
      }
    },
    [updateListMutation],
  );
  const deleteListFromDb = useCallback(
    async (id: string) => {
      try {
        await deleteListMutation.mutateAsync(id);
        return true;
      } catch {
        return false;
      }
    },
    [deleteListMutation],
  );
  const moveListTasks = useCallback(
    async (fromListId: string, toListId: string) => {
      try {
        await moveListTasksMutation.mutateAsync({ fromListId, toListId });
        return true;
      } catch {
        return false;
      }
    },
    [moveListTasksMutation],
  );
  const deleteAllTasksInList = useCallback(
    async (listId: string) => {
      try {
        await deleteAllTasksInListMutation.mutateAsync(listId);
        return true;
      } catch {
        return false;
      }
    },
    [deleteAllTasksInListMutation],
  );

  // Tag adapters that preserve the (data) / (id, data) call signatures
  // expected by useListTagManagement and the components that still use them.
  const addTagToDb = useCallback(
    async (data: { name: string; color: string }) => {
      try {
        return await createTagMutation.mutateAsync(data);
      } catch {
        return null;
      }
    },
    [createTagMutation],
  );
  const updateTagInDb = useCallback(
    async (id: string, updates: { name?: string; color?: string }) => {
      try {
        await updateTagMutation.mutateAsync({ id, updates });
        return true;
      } catch {
        return false;
      }
    },
    [updateTagMutation],
  );
  const deleteTagFromDb = useCallback(
    async (id: string) => {
      try {
        await deleteTagMutation.mutateAsync(id);
        return true;
      } catch {
        return false;
      }
    },
    [deleteTagMutation],
  );

  // Filter adapters that preserve the (data) / (id, data) call signatures.
  const addFilterToDb = useCallback(
    async (data: Parameters<typeof createFilterMutation.mutateAsync>[0]) => {
      try {
        return await createFilterMutation.mutateAsync(data);
      } catch {
        return null;
      }
    },
    [createFilterMutation],
  );
  const updateFilterInDb = useCallback(
    async (id: string, updates: Parameters<typeof updateFilterMutation.mutateAsync>[0]["updates"]) => {
      try {
        await updateFilterMutation.mutateAsync({ id, updates });
        return true;
      } catch {
        return false;
      }
    },
    [updateFilterMutation],
  );

  const getTagTaskCount = useCallback(
    (tagId: string) => countTasksWithTag(tasks, tagId),
    [tasks],
  );

  // Adapter preserving the (newTask, options?) => Task | null signature the
  // voice-review and Captures flows branch on. The hook reports its own
  // failure via Sentry and a toast, so the null is only a control-flow signal.
  const addTaskToDb = useCallback(
    async (newTask: NewTask, options?: { silent?: boolean }) => {
      try {
        return await createTaskMutation.mutateAsync({ newTask, options });
      } catch {
        return null;
      }
    },
    [createTaskMutation],
  );

  const addSubtaskToDb = useCreateSubtask();
  const updateSubtaskInDb = useUpdateSubtask();
  const moveSubtaskInDb = useMoveSubtask();
  const convertSubtaskToTaskInDb = useConvertSubtaskToTask();
  const convertTaskToSubtaskInDb = useConvertTaskToSubtask();

  const setTaskTags = useSetTaskTags();
  const setSubtaskTags = useSetSubtaskTags();

  const dialog = useDialog();

  const {
    editingList,
    setEditingList,
    editingTag,
    setEditingTag,
    editingFilter,
    setEditingFilter,
    newListName,
    setNewListName,
    newListEmoji,
    setNewListEmoji,
    newTagName,
    setNewTagName,
    newTagColor,
    setNewTagColor,
    addList,
    updateList,
    buildListBreakdown,
    deleteList,
    addTag,
    updateTag,
    deleteTag,
    handleSaveFilter,
  } = useListTagManagement({
    tasks,
    lists,
    tags,
    dialog,
    addListToDb,
    updateListInDb,
    deleteListFromDb,
    moveListTasks,
    deleteAllTasksInList,
    addTagToDb,
    updateTagInDb,
    deleteTagFromDb,
    addFilterToDb,
    updateFilterInDb,
  });

  const [newTask, setNewTask] = useState<NewTask>(emptyNewTask);
  // Ordered visible tasks from the board — updated via BoardView's
  // onVisibleTasksChange callback and fed into useTaskEditing for prev/next
  // navigation while a task dialog is open.
  const [boardVisibleTasks, setBoardVisibleTasks] = useState<Task[]>([]);
  // Bumped each time the search dialog closes with a non-empty term, so the
  // header chip can replay an attention-grabbing animation. Drawing the eye
  // helps users notice the search is still narrowing what's on the board.
  const [searchHighlightKey, setSearchHighlightKey] = useState(0);
  const { pathname } = useLocation();

  // Guard against deep-linking to /emails without a connected Gmail account.
  // Wait until the connection status finishes loading so we don't redirect
  // prematurely on first paint or during the OAuth callback.
  useEffect(() => {
    if (viewType === "emails" && !isLoadingGmailConnection && !gmailConnected) {
      navigate({ to: "/" });
    }
  }, [viewType, isLoadingGmailConnection, gmailConnected, navigate]);

  // Calendar events (only fetched when calendar view is active and connected)
  const {
    events: calendarEvents,
    isLoading: isLoadingCalendarEvents,
    isAuthExpired: isCalendarAuthExpired,
    fetchEvents: fetchCalendarEvents,
    updateExternalEvent,
    createStandaloneEvent,
  } = useCalendarEvents();

  useConnectionToasts({
    isCalendarAuthExpired,
    connectGoogleCalendar,
    gmailReconnectNeeded,
    connectGmail,
  });

  // Local, not UTC: scheduled_date is a local calendar date, so toISOString()
  // would report the wrong day around midnight for non-UTC users.
  const today = formatLocalDate(new Date());
  const unscheduledTasks = useMemo(
    () =>
      tasks.filter(
        (t) =>
          !t.completed &&
          !t.archived &&
          !t.recurrence_frequency &&
          (!t.scheduled_date || t.scheduled_date < today),
      ),
    [tasks, today],
  );

  const handleCheckout = async (priceId: string) => {
    const url = await createCheckoutSession(priceId);
    if (url) {
      window.location.href = url;
    }
  };

  const handleManageSubscription = async () => {
    const url = await createPortalSession();
    if (url) {
      window.location.href = url;
    }
  };

  // Derive showEditDialog from URL
  const showEditDialog = !!taskId;

  const {
    exitAnimatingTaskId,
    pendingComplete,
    beginPendingComplete,
    cancelPendingComplete,
    confirmCompleteWithSubtasks,
    isNavigatingTask,
    viewBeforeTaskOpen,
  } = useTaskExitAnimation({
    tasks,
    taskId,
    showEditDialog,
    updateSubtaskInDb: updateSubtaskInDb,
    toggleTaskInDb: tqToggleTask,
  });

  const {
    isRecording,
    recordingDuration,
    analyserNode,
    startRecording,
    stopRecording,
    isProcessingVoice,
    transcript,
    proposedTodos,
    showVoiceNoResults,
    handleAcceptVoiceTodos,
    clearVoiceResults,
  } = useVoiceIntegration({ lists, tags, addTask: addTaskToDb, addSubtask: addSubtaskToDb, setSubtaskTags });

  const handleApproveCapture = useCallback(
    async (_capture: TaskCapture, todos: ProposedTodo[]) => {
      await saveReviewedTodos(todos, { addTask: addTaskToDb, addSubtask: addSubtaskToDb, setSubtaskTags });
    },
    [addTaskToDb, addSubtaskToDb, setSubtaskTags],
  );

  useCheckoutPolling({ isPro, refetch: refetchSubscription });

  useGmailOAuthCallback(handleGmailOAuthCallback);

  useRealtimeSync();

  // Global keyboard shortcuts — all TodoApp-scoped shortcuts in one declarative call.
  useTodoAppGlobalShortcuts({
    searchTerm,
    activeFilterId,
    hasBuiltInFilters,
    dialog,
    showEditDialog,
    viewType,
    toggleShowCompleted,
    toggleAllSubtasksExpanded,
    builtInFilterState,
    lists,
    isRecording,
    stopRecording,
    startRecording,
    navigate,
    setSearchTerm,
    gmailConnected,
  });

  const addTask = async (options?: { silent?: boolean }): Promise<Task | null> => {
    if (!newTask.title.trim()) return null;

    const created = await createTaskMutation.mutateAsync({ newTask, options }).catch(() => null);
    if (!created) return null;
    setNewTask({
      ...emptyNewTask(),
      list_id: newTask.list_id,
      priority: newTask.priority,
    });
    dialog.close();
    return created;
  };

  const quickAddTask = async (title: string) => {
    if (!title.trim()) return;
    const parsed = parseDateFromTitle(title);
    createTaskMutation.mutate({
      newTask: {
        title: parsed.title.trim(),
        list_id: null,
        priority: 4,
        description: "",
        scheduled_date: parsed.date,
        scheduled_time: parsed.time,
        deadline_date: null,
        duration: null,
        subtasks: [],
        tag_ids: [],
        links: [],
      },
    });
  };

  const toggleTask = async (id: string) => {
    const task = tasks.find((t) => t.id === id);
    if (!task) return;
    const hasIncompleteSubtasks = !!task.subtasks?.some((s) => !s.completed);
    const isTransitioningToComplete = !task.completed;
    if (isTransitioningToComplete && hasIncompleteSubtasks) {
      beginPendingComplete(id);
      return;
    }
    await tqToggleTask(id);
  };

  const {
    editingTask,
    setEditingTask,
    editingTaskTagIds,
    setEditingTaskTagIds,
    setSearchJumpedFromTaskId,
    taskNotFound,
    convertingTaskId,
    setConvertingTaskId,
    pendingRecurringChange,
    setPendingRecurringChange,
    canNavigate,
    updateTask,
    handleEditTask,
    handleCloseEditDialog,
    handleToggleCompleteFromDialog,
    handleArchiveTask,
    handleDeleteTask,
    confirmDeleteTask,
    handleDeleteSubtask,
    confirmDeleteSubtask,
    handleNavigateTask,
    handleMoveSubtask,
    handleConvertSubtaskToTask,
    handleConvertTaskToSubtask,
    handleSubtaskNavigate,
  } = useTodoTaskEditing({
    tasks,
    taskId,
    isLoading,
    navigate,
    pathname,
    dialog,
    viewBeforeTaskOpen,
    visibleTasks: boardVisibleTasks,
    clearSearchTerm: () => setSearchTerm(""),
    mutations: {
      toggleTaskInDb: tqToggleTask,
      updateTaskInDb: tqUpdateTask,
      updateSubtaskInDb,
      deleteTaskFromDb: tqDeleteTask,
      archiveTaskInDb: tqArchiveTask,
      addSubtaskToDb,
      deleteSubtaskFromDb: tqDeleteSubtask,
      deleteSubtasksFromDb: tqDeleteSubtasks,
      moveSubtaskInDb,
      convertSubtaskToTaskInDb,
      convertTaskToSubtaskInDb,
      setTaskTags,
    },
  });

  if (isLoading) {
    return <AppLoader />;
  }

  return (
    <div className="min-h-screen bg-warm-bg-base">
      <Header
        onOpenSettings={() => dialog.open("settings")}
        searchTerm={searchTerm}
        searchHighlightKey={searchHighlightKey}
        onSearchClear={() => setSearchTerm("")}
        onSearchClick={() => dialog.open("search")}
        viewType={viewType}
        onViewTypeChange={(newView) =>
          navigate({
            to:
              newView === "calendar"
                ? "/calendar"
                : newView === "emails"
                  ? "/emails"
                  : newView === "captures"
                    ? "/captures"
                    : "/",
          })
        }
        gmailAccounts={gmailAccounts}
        gmailAccountCounts={gmailAccountCounts}
        gmailConnected={gmailConnected}
      />

      {viewType === "emails" ? (
        <Suspense fallback={<ViewLoadingSkeleton />}>
          <EmailsViewShell
            gmailAccounts={gmailAccounts}
            activeGmailAccount={activeGmailAccount}
            setActiveGmailAccount={handleGmailAccountChange}
            gmailThreads={gmailThreads}
            isFetchingGmail={isFetchingGmail}
            fetchGmailEmails={fetchGmailEmails}
            fetchGmailThread={fetchGmailThread}
            fetchGmailAttachment={fetchGmailAttachment}
            archiveGmailEmails={archiveGmailEmails}
            tasks={tasks}
            lists={lists} tags={tags}
            userEmail={user?.email ?? null}
            userAvatar={user?.avatar ?? null}
            isGoogleCalendarConnected={isGoogleCalendarConnected}
            createStandaloneEvent={createStandaloneEvent}
            dialog={dialog}
            showEditDialog={showEditDialog}
            onConvertToTask={(task, messageIds) => {
              setNewTask(task);
              dialog.open("add-task", messageIds);
            }}
            handleEditTask={handleEditTask} {...emailPageUrl.shellProps}
          />
        </Suspense>
      ) : viewType === "captures" ? (
        <Suspense fallback={<ViewLoadingSkeleton />}>
          <CapturesView
            lists={lists}
            tags={tags}
            tasks={tasks}
            onApprove={handleApproveCapture}
          />
        </Suspense>
      ) : viewType === "board" ? (
        <BoardView
          tasks={tasks}
          lists={lists}
          tags={tags}
          filters={filters}
          dialog={dialog}
          showEditDialog={showEditDialog}
          setEditingFilter={setEditingFilter}
          buildListBreakdown={buildListBreakdown}
          handleEditTask={handleEditTask}
          exitAnimatingTaskId={exitAnimatingTaskId}
          setConvertingTaskId={setConvertingTaskId}
          toggleTask={toggleTask}
          setNewTask={setNewTask}
          quickAddTask={quickAddTask}
          onVisibleTasksChange={setBoardVisibleTasks}
        />
      ) : (
        <Suspense fallback={<CalendarViewSkeleton />}>
          <CalendarView
            tasks={tasks.filter((t) => !t.archived)}
            unscheduledTasks={unscheduledTasks}
            lists={lists}
            tags={tags}
            builtInFilterState={builtInFilterState}
            onBuiltInFilterChange={setBuiltInFilterState}
            shortcutHintsActive={dialog.isOpen("shortcut-hints")}
            isTaskDialogOpen={showEditDialog}
            calendarEvents={isGoogleCalendarConnected ? calendarEvents : []}
            isLoadingEvents={isLoadingCalendarEvents}
            onFetchEvents={
              isGoogleCalendarConnected ? fetchCalendarEvents : async () => {}
            }
            onUpdateExternalEvent={updateExternalEvent}
            onTaskClick={handleEditTask}
            weekStartDay={googleCalendarConnection?.week_start_day ?? null}
            onSubtaskClick={handleEditTask}
          />
        </Suspense>
      )}

      <AnimatePresence>
        {dialog.isOpen("add-task") && (
          <TaskDialog
            mode="add"
            task={newTask}
            onTaskChange={setNewTask}
            lists={lists}
            tags={tags}
            onClose={() => {
              setNewTask(emptyNewTask());
              dialog.close();
            }}
            onSave={async () => {
              // Snapshot payload before save since dialog.close() clears it
              const messageIdsToArchive = dialog.payloadFor("add-task") ?? null;
              const created = await addTask(
                messageIdsToArchive && messageIdsToArchive.length > 0
                  ? { silent: true }
                  : undefined,
              );
              if (
                created &&
                messageIdsToArchive &&
                messageIdsToArchive.length > 0
              ) {
                await archiveGmailEmails(messageIdsToArchive, createdFromEmailLabels(
                  { prefix: "Task ", label: `"${created.title}"`, suffix: " created", onClick: () => handleEditTask(created) },
                  messageIdsToArchive.length === 1 ? "email" : "conversation",
                ));
              }
            }}
            userId={user?.id}
            allTasksForPicker={tasks.filter((t) => !t.archived)}
            calendarEventsForPicker={
              isGoogleCalendarConnected ? calendarEvents : []
            }
            onFetchCalendarEvents={
              isGoogleCalendarConnected ? fetchCalendarEvents : async () => {}
            }
            isLoadingCalendarEvents={isLoadingCalendarEvents}
            weekStartDay={googleCalendarConnection?.week_start_day ?? null}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditDialog && editingTask && !taskNotFound && (
          <TaskDialog
            key={taskId}
            mode="edit"
            task={editingTask}
            onTaskChange={setEditingTask}
            lists={lists}
            tags={tags}
            selectedTagIds={editingTaskTagIds}
            onTagsChange={setEditingTaskTagIds}
            onClose={handleCloseEditDialog}
            // When a dialog opens on top of the task (e.g. the keyboard-
            // shortcuts help via "?"), Escape should dismiss that dialog, not
            // close the task underneath it.
            hasOverlayAbove={dialog.current !== null}
            onSave={updateTask}
            userId={user?.id}
            canNavigate={canNavigate}
            allTasksForMove={tasks.filter((t) => !t.archived)}
            onNavigateNext={() => handleNavigateTask("next")}
            onNavigatePrev={() => handleNavigateTask("prev")}
            isNavigating={isNavigatingTask}
            onDelete={handleDeleteTask}
            onDeleteSubtask={handleDeleteSubtask}
            onArchive={handleArchiveTask}
            onToggleComplete={handleToggleCompleteFromDialog}
            onMoveSubtask={handleMoveSubtask}
            onConvertSubtaskToTask={handleConvertSubtaskToTask}
            onConvertToSubtask={setConvertingTaskId}
            activeSubtaskId={activeSubtaskId ?? null}
            onActiveSubtaskChange={handleSubtaskNavigate}
            allTasksForPicker={tasks.filter((t) => !t.archived)}
            calendarEventsForPicker={
              isGoogleCalendarConnected ? calendarEvents : []
            }
            onFetchCalendarEvents={
              isGoogleCalendarConnected ? fetchCalendarEvents : async () => {}
            }
            isLoadingCalendarEvents={isLoadingCalendarEvents}
            weekStartDay={googleCalendarConnection?.week_start_day ?? null}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>{taskNotFound && <TaskNotFound />}</AnimatePresence>

      <AnimatePresence>
        {dialog.isOpen("add-list") && (
          <AddListDialog
            newListName={newListName}
            setNewListName={setNewListName}
            newListEmoji={newListEmoji}
            setNewListEmoji={setNewListEmoji}
            onClose={() => dialog.close()}
            onAdd={addList}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {dialog.isOpen("add-tag") && (
          <AddTagDialog
            newTagName={newTagName}
            setNewTagName={setNewTagName}
            newTagColor={newTagColor}
            setNewTagColor={setNewTagColor}
            onClose={() => dialog.close()}
            onAdd={addTag}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {dialog.isOpen("settings") && (
          <SettingsDialog
            lists={lists}
            tasks={tasks}
            editingList={editingList}
            setEditingList={setEditingList}
            onUpdateList={updateList}
            onDeleteListClick={(name) => {
              const breakdown = buildListBreakdown(name);
              dialog.open("delete-confirm", {
                type: "list",
                name,
                taskCount:
                  breakdown.open + breakdown.completed + breakdown.archived,
                listBreakdown: breakdown,
              });
            }}
            onAddList={() => dialog.open("add-list")}
            tags={tags}
            getTagTaskCount={getTagTaskCount}
            editingTag={editingTag}
            setEditingTag={setEditingTag}
            onUpdateTag={updateTag}
            onDeleteTagClick={(tag) => {
              const taskCount = getTagTaskCount(tag.id);
              dialog.open("delete-confirm", {
                type: "tag",
                name: tag.name,
                id: tag.id,
                taskCount,
              });
            }}
            onAddTag={() => dialog.open("add-tag")}
            isPro={isPro}
            subscriptionPlan={subscriptionPlan}
            subscription={subscription}
            onUpgradeClick={() => dialog.replace("upgrade", "general")}
            onManageSubscription={handleManageSubscription}
            gmailConnected={gmailConnected}
            onClose={() => {
              dialog.close();
              reloadGmailAccounts();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {dialog.isOpen("delete-confirm") && (() => {
          const dc = dialog.payloadFor("delete-confirm");
          if (!dc) return null;
          return (
            <DeleteConfirmDialog
              listName={dc.name}
              taskCount={dc.taskCount}
              listBreakdown={dc.listBreakdown}
              itemType={dc.type}
              lists={lists}
              onCancel={() => dialog.close()}
              onConfirm={(action) => {
                if (dc.type === "list") {
                  deleteList(dc.name, action);
                } else if (dc.type === "tag") {
                  deleteTag(dc.id!);
                } else if (dc.type === "task") {
                  confirmDeleteTask(dc.id!);
                } else if (dc.type === "subtask") {
                  confirmDeleteSubtask(dc.id!);
                }
              }}
            />
          );
        })()}
      </AnimatePresence>

      <AnimatePresence>
        {pendingRecurringChange && (
          <RecurringChangeWarningDialog
            task={pendingRecurringChange.task}
            onCancel={() => {
              pendingRecurringChange.resolve(false);
              setPendingRecurringChange(null);
            }}
            onConfirm={() => {
              pendingRecurringChange.resolve(true);
              setPendingRecurringChange(null);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {pendingComplete &&
          (() => {
            const pendingTask = tasks.find(
              (t) => t.id === pendingComplete.taskId,
            );
            if (!pendingTask) return null;
            const incomplete =
              pendingTask.subtasks?.filter((s) => !s.completed) ?? [];
            return (
              <CompleteWithSubtasksDialog
                taskTitle={pendingTask.title}
                incompleteSubtasks={incomplete}
                onCancel={cancelPendingComplete}
                onConfirm={confirmCompleteWithSubtasks}
                onOpenTask={() => {
                  cancelPendingComplete();
                  handleEditTask(pendingTask);
                }}
                onOpenSubtask={(subtaskId) => {
                  cancelPendingComplete();
                  viewBeforeTaskOpen.current = pathname;
                  navigate({
                    to: "/task/$taskId",
                    params: { taskId: pendingTask.id },
                    search: (prev: Record<string, unknown>) => ({
                      ...prev,
                      subtask: subtaskId,
                    }),
                  });
                }}
              />
            );
          })()}
      </AnimatePresence>

      <AnimatePresence>
        {dialog.isOpen("filter") && (
          <FilterDialog
            filter={editingFilter}
            lists={lists}
            tags={tags}
            onClose={() => {
              dialog.close();
              setEditingFilter(null);
            }}
            onSave={handleSaveFilter}
            isEditing={editingFilter !== null}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {dialog.isOpen("search") && (
          <SearchDialog
            tasks={tasks.filter((t) => !t.archived)}
            onClose={() => {
              dialog.close();
              if (searchTerm.trim()) {
                setSearchHighlightKey((k) => k + 1);
              }
            }}
            onSelectTask={(task) => {
              dialog.close();
              // Search opened on top of an existing task dialog: treat the
              // pick as a side-trip. Don't update viewBeforeTaskOpen — we
              // need it to keep pointing at the underlying view so the
              // second Escape (from the original task) closes normally.
              if (taskId && task.id !== taskId) {
                setSearchJumpedFromTaskId(taskId);
                navigate({
                  to: "/task/$taskId",
                  params: { taskId: task.id },
                  search: (prev: Record<string, unknown>) => prev,
                });
                return;
              }
              if (taskId && task.id === taskId) return; // already viewing it
              handleEditTask(task);
            }}
            initialSearchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
          />
        )}
      </AnimatePresence>

      {convertingTaskId && (
        <TaskPickerDialog
          key={convertingTaskId}
          tasks={tasks.filter((t) => !t.archived)}
          lists={lists}
          excludeTaskIds={[convertingTaskId]}
          onClose={() => setConvertingTaskId(null)}
          onSelect={(selection) => {
            const sourceId = convertingTaskId;
            setConvertingTaskId(null);
            if (sourceId) {
              handleConvertTaskToSubtask(sourceId, selection.task.id);
            }
          }}
        />
      )}

      <AnimatePresence>
        {dialog.isOpen("voice-review") && transcript && (
          <VoiceReviewDialog
            transcript={transcript}
            proposedTodos={proposedTodos}
            lists={lists}
            tags={tags}
            tasks={tasks}
            onClose={() => {
              dialog.close();
              clearVoiceResults();
            }}
            onAccept={handleAcceptVoiceTodos}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {dialog.isOpen("shortcuts") && (
          <KeyboardShortcutsDialog
            onClose={() => dialog.close()}
            currentView={viewType}
            taskDialogOpen={showEditDialog}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {dialog.isOpen("upgrade") && (
          <UpgradeDialog
            reason={dialog.payloadFor("upgrade") ?? "general"}
            onClose={() => dialog.close()}
            onCheckout={handleCheckout}
          />
        )}
      </AnimatePresence>

      {/* Shortcut-hints overlay disabled for now — not available yet. Re-enable
          alongside the Cmd/Ctrl+/ handler above when the overlay ships. */}
      {/* {dialog.isOpen("shortcut-hints") && (
        <ShortcutHintsOverlay onClose={() => dialog.close()} />
      )} */}

      <FloatingActionButton
        onClick={() => {
          dialog.open("add-task", null);
        }}
        shortcutKey="N"
        isRecording={isRecording}
        isProcessingVoice={isProcessingVoice}
        recordingDuration={recordingDuration}
        analyserNode={analyserNode}
        showVoiceNoResults={showVoiceNoResults}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
      />
      <PWAInstallPrompt />
    </div>
  );
};

// The app shell is split in two so the view state can be owned by a provider
// rather than by the shell: `TodoApp` mounts `ViewProvider`, and everything
// under it — including the shell itself — reads that state through `useView()`.
const TodoApp = () => (
  <ViewProvider>
    <TodoAppContent />
  </ViewProvider>
);

export default TodoApp;
